import React from 'react';
import { Empty, Spin, Pagination } from 'antd';
import styled from 'styled-components';

/**
 * Renders a list of records as stacked cards — used as a mobile-friendly
 * replacement for a wide AntD table.
 *
 * Props:
 *   records      — array of items to render
 *   rowKey       — string key (default '_id')
 *   loading      — boolean
 *   onRowClick   — optional click handler for the whole card
 *   renderCard   — (record) => ReactNode  (the body of each card)
 *   emptyText    — optional message when records is empty
 *   pagination   — optional AntD-pagination-shaped object: { current, pageSize, total, onChange }
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: ${({ $clickable }) =>
      $clickable ? '0 4px 12px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.04)'};
  }
`;

const PaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
`;

const MobileCardList = ({
  records = [],
  rowKey = '_id',
  loading = false,
  onRowClick,
  renderCard,
  emptyText = 'No data',
  pagination
}) => {
  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  }

  if (!records.length) {
    return <Empty description={emptyText} style={{ padding: 32 }} />;
  }

  return (
    <>
      <Wrapper>
        {records.map((r) => (
          <Card
            key={r[rowKey]}
            $clickable={!!onRowClick}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
          >
            {renderCard(r)}
          </Card>
        ))}
      </Wrapper>
      {pagination && (
        <PaginationWrap>
          <Pagination
            simple
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
          />
        </PaginationWrap>
      )}
    </>
  );
};

export default MobileCardList;
